import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Ledger.css'; 
import baseURL from '../../../../Url/NodeBaseURL';

function Ledger() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedView, setSelectedView] = useState('customers');
  const [ledgerData, setLedgerData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [accountDetails, setAccountDetails] = useState([]);
  const [summary, setSummary] = useState(null);

  // Fetch all account details
  const fetchAccountDetails = async () => {
    try {
      const response = await axios.get(`${baseURL}/get/account-details`);
      setAccountDetails(response.data);
      
      // Filter customers (account_group = 'CUSTOMERS')
      const customerList = response.data.filter(
        item => item.account_group === 'CUSTOMERS'
      );
      setCustomers(customerList);
    } catch (err) {
      console.error('Error fetching account details:', err);
    }
  };

  // Fetch ledger data based on selected view
  const fetchLedgerData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let data = [];
      
      // Determine which account to fetch based on selection
      if (selectedView === 'customers') {
        // Fetch all customer accounts
        for (const customer of customers) {
          try {
            const response = await axios.get(`${baseURL}/ledger/account/${customer.account_id}`);
            const entries = response.data.map(entry => ({
              ...entry,
              customer_name: customer.account_name,
              customer_id: customer.account_id
            }));
            data.push(...entries);
          } catch (err) {
            console.error(`Error fetching ledger for customer ${customer.account_id}:`, err);
          }
        }
      } else {
        // For Sales, CGST, SGST - get account_id from account_details
        const accountMap = {
          'sales': 'Sales',
          'cgst': 'CGST',
          'sgst': 'SGST'
        };
        
        const accountNameToFind = accountMap[selectedView];
        const account = accountDetails.find(
          item => item.account_name === accountNameToFind
        );
        
        if (account) {
          const response = await axios.get(`${baseURL}/ledger/account/${account.account_id}`);
          data = response.data;
        } else {
          setError(`Account "${accountNameToFind}" not found`);
          setLoading(false);
          return;
        }
      }
      
      // Sort by date (newest first)
      data.sort((a, b) => new Date(b.transaction_date) - new Date(a.transaction_date));
      
      setLedgerData(data);
      setSummary({
        totalEntries: data.length,
        totalDebit: data.reduce((sum, item) => sum + parseFloat(item.debit || 0), 0),
        totalCredit: data.reduce((sum, item) => sum + parseFloat(item.credit || 0), 0)
      });
    } catch (err) {
      console.error('Error fetching ledger data:', err);
      setError('Failed to fetch ledger data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle view change
  const handleViewChange = (e) => {
    setSelectedView(e.target.value);
    setLedgerData([]);
    setSummary(null);
  };

  // Initial load
  useEffect(() => {
    fetchAccountDetails();
  }, []);

  // Fetch ledger data when view changes or customers load
  useEffect(() => {
    if (selectedView === 'customers' && customers.length > 0) {
      fetchLedgerData();
    } else if (selectedView !== 'customers' && accountDetails.length > 0) {
      fetchLedgerData();
    }
  }, [selectedView, customers, accountDetails]);

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0.00';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get entry type label
  const getEntryLabel = (entry) => {
    if (entry.short_narrative) return entry.short_narrative;
    if (entry.account_name === 'Sales') return 'Sales';
    if (entry.account_name === 'CGST') return 'CGST';
    if (entry.account_name === 'SGST') return 'SGST';
    if (entry.dc === 'D') return 'Debit';
    if (entry.dc === 'C') return 'Credit';
    return 'Transaction';
  };

  // Get balance color
  const getBalanceColor = (balance) => {
    if (balance === null || balance === undefined) return '';
    const numBalance = parseFloat(balance);
    if (numBalance > 0) return 'balance-debit';
    if (numBalance < 0) return 'balance-credit';
    return 'balance-zero';
  };

  // Get view label
  const getViewLabel = () => {
    switch(selectedView) {
      case 'customers': return 'All Customers';
      case 'sales': return 'Sales Account';
      case 'cgst': return 'CGST Account';
      case 'sgst': return 'SGST Account';
      default: return 'Ledger';
    }
  };

  // Render dropdown options
  const renderViewOptions = () => {
    const options = [
      { value: 'customers', label: '👥 All Customers' },
      { value: 'sales', label: '💰 Sales' },
      { value: 'cgst', label: '📊 CGST' },
      { value: 'sgst', label: '📊 SGST' }
    ];
    
    return options.map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ));
  };

  return (
    <div className="ledger-page">
      <div className="ledger-container">
        {/* Header with Dropdown */}
        <div className="ledger-header-main">
          <h2>📒 Ledger Account</h2>
          <div className="view-selector">
            <label htmlFor="viewSelect">Select Account:</label>
            <select 
              id="viewSelect"
              value={selectedView} 
              onChange={handleViewChange}
              className="view-select"
            >
              {renderViewOptions()}
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && summary.totalEntries > 0 && (
          <div className="summary-cards">
            <div className="summary-card">
              <label>Total Transactions</label>
              <span>{summary.totalEntries}</span>
            </div>
            <div className="summary-card">
              <label>Total Debit</label>
              <span className="balance-debit">{formatCurrency(summary.totalDebit)}</span>
            </div>
            <div className="summary-card">
              <label>Total Credit</label>
              <span className="balance-credit">{formatCurrency(summary.totalCredit)}</span>
            </div>
            <div className="summary-card highlight">
              <label>Net Balance</label>
              <span className={getBalanceColor(summary.totalDebit - summary.totalCredit)}>
                {formatCurrency(summary.totalDebit - summary.totalCredit)}
              </span>
            </div>
          </div>
        )}

        {/* Loading and Error States */}
        {loading && (
          <div className="loading-spinner">Loading ledger data...</div>
        )}
        
        {error && (
          <div className="error-message">{error}</div>
        )}

        {/* Ledger Table */}
        {!loading && !error && (
          <div className="ledger-content">
            {ledgerData.length === 0 ? (
              <div className="no-data">No transactions found for {getViewLabel()}</div>
            ) : (
              <div className="ledger-table-wrapper">
                <table className="ledger-table">
                  <thead>
                    <tr>
                      <th>Invoice No.</th>
                      <th>Date</th>
                      <th>Account Name</th>
                      <th>Description</th>
                      <th>DC</th>
                      <th>Amount</th>
                      <th>Debit (Dr)</th>
                      <th>Credit (Cr)</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerData.map((entry, index) => (
                      <tr key={index} className={entry.dc === 'D' ? 'entry-debit-row' : 'entry-credit-row'}>
                        <td>
                          <span className="invoice-number-cell">
                            {entry.invoice_number || '-'}
                          </span>
                        </td>
                        <td>{formatDate(entry.transaction_date)}</td>
                        <td>
                          <span className="account-name">
                            {entry.account_name}
                            {entry.customer_name && (
                              <span className="customer-tag">({entry.customer_name})</span>
                            )}
                          </span>
                        </td>
                        <td>{getEntryLabel(entry)}</td>
                        <td>
                          <span className={`dc-badge ${entry.dc === 'D' ? 'dc-debit' : 'dc-credit'}`}>
                            {entry.dc || '-'}
                          </span>
                        </td>
                        <td className="amount-cell">{formatCurrency(entry.amount)}</td>
                        <td className="debit-amount">
                          {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                        </td>
                        <td className="credit-amount">
                          {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                        </td>
                        <td className={getBalanceColor(entry.balance)}>
                          {formatCurrency(entry.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Ledger;